import React, { memo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { logger } from 'src/utils/logger'
import {
  ContractorPortalUploadDebitCard,
  ContractorPortalUploadBankInformation,
} from 'src/constants/actionTypes'
import { BankPaymentForm } from 'src/components/bank-payment-form/BankPaymentForm'
import {
  BankInformationRequest,
  DebitCardRequest,
} from 'src/components/bank-payment-form/BankPaymentForm.interface'
import { DriverPaymentMethod } from 'src/interfaces/paymentMethod'

interface ContractorPortalBankPaymentMethodFormProps {
  handleFailure?: () => void
  handleSuccess?: () => void
  paymentMethodData?: DriverPaymentMethod | null
  verificationToken?: string | null
}

const ContractorPortalBankPaymentMethodFormComponent = ({
  handleFailure,
  handleSuccess,
  paymentMethodData,
  verificationToken,
}: ContractorPortalBankPaymentMethodFormProps) => {
  const dispatch = useDispatch()

  const handleSubmitDebitCard = useCallback(
    async (requestBody: DebitCardRequest) => {
      try {
        await createAsyncAction(
          dispatch,
          ContractorPortalUploadDebitCard.request({
            ...requestBody,
            token: verificationToken,
          }),
        )

        if (handleSuccess) {
          handleSuccess()
        }
      } catch (error) {
        logger.error('Failed to submit debit card', error)

        if (handleFailure) {
          handleFailure()
        }
      }
    },
    [dispatch, handleFailure, handleSuccess, verificationToken],
  )

  const handleSubmitBankInformation = useCallback(
    async (requestBody: BankInformationRequest) => {
      try {
        await createAsyncAction(
          dispatch,
          ContractorPortalUploadBankInformation.request({
            ...requestBody,
            token: verificationToken,
          }),
        )

        if (handleSuccess) {
          handleSuccess()
        }
      } catch (error) {
        logger.error('Failed to submit bank information', error)

        if (handleFailure) {
          handleFailure()
        }
      }
    },
    [dispatch, handleFailure, handleSuccess, verificationToken],
  )

  return (
    <BankPaymentForm
      title="Update settlement method"
      showIcon={false}
      showStepIndex={false}
      showCurrentPaymentMethod={false}
      showFormLabel={false}
      onSubmitDebitCard={handleSubmitDebitCard}
      onSubmitBankInformation={handleSubmitBankInformation}
      paymentMethodData={paymentMethodData}
      flow="contractorPortal"
    />
  )
}

export const ContractorPortalBankPaymentMethodForm = memo(
  ContractorPortalBankPaymentMethodFormComponent,
)
